import React, { useRef, useState } from "react";
import styled from "styled-components";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import { QuestionaireProps, QuestionType } from "./types";
import QuestionsMain from "./QuestionsMain";
import QuestionsSecondary from "./QuestionsSecondary";
import AdditionalQuestionsPrompt from "./AdditionalQuestionsPrompt";
import {
  generateEmailMessage,
  validateDate,
  validateEmail,
  validatePhoneNumber,
} from "./utils";

// Where the form POSTs. A DigitalOcean App Platform Function (same origin as
// the site) gates on Origin + a hidden honeypot + a minimum fill-time, then
// sends the email via Resend — the mail credential never reaches the browser
// bundle. No third-party widget, so the page console stays clean.
const CONTACT_ENDPOINT =
  process.env.REACT_APP_CONTACT_ENDPOINT || "/api/contact/send";

// Full-width blush-tint band — the inquiry section background from the design.
const QuestionaireWrapper = styled.div`
  background-color: var(--jm-blush-tint);
  padding: 80px 24px;

  @media (min-width: 768px) {
    padding: 104px 40px;
  }
`;

// Asymmetric two-column split: editorial intro on the left, form card on the
// right. Collapses to a single column under 900px.
const Layout = styled.div`
  max-width: 1040px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr;
  gap: 40px;
  align-items: start;

  @media (min-width: 900px) {
    grid-template-columns: 0.82fr 1.18fr;
    gap: 56px;
  }
`;

const Intro = styled.div`
  @media (min-width: 900px) {
    position: sticky;
    top: 96px;
  }
`;

const Eyebrow = styled.div`
  font-family: var(--jm-sans);
  font-weight: 600;
  font-size: 13px;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: var(--jm-rose-deep);
  margin-bottom: 14px;
`;

const IntroHeading = styled.h2`
  font-family: var(--jm-display);
  font-weight: 500;
  font-size: clamp(30px, 3.4vw, 42px);
  line-height: 1.1;
  color: var(--jm-ink);
  margin: 0 0 18px;
`;

const IntroCopy = styled.p`
  font-family: var(--jm-sans);
  font-size: 16px;
  line-height: 1.65;
  color: var(--jm-ink-soft);
  max-width: 42ch;
  margin: 0;
`;

// Ivory form card — soft warm shadow, hairline border, generous padding.
const Card = styled.div`
  background-color: var(--jm-ivory);
  border: 1px solid var(--jm-line);
  border-radius: 16px;
  box-shadow: var(--jm-sh-md);
  padding: 24px;

  @media (min-width: 768px) {
    padding: 32px;
  }
`;

const ButtonStyled = styled(Button)`
  && {
    width: 100%;
    font-family: var(--jm-sans);
    font-weight: 600;
    font-size: 13px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #f4efe6;
    background-color: var(--jm-sage-deep);
    border-color: var(--jm-sage-deep);
    border-radius: 999px;
    padding: 14px 30px;
    transition: background-color 0.3s var(--jm-ease),
      border-color 0.3s var(--jm-ease);
  }
  &&:hover,
  &&:focus,
  &&:active {
    background-color: var(--jm-sage-hover);
    border-color: var(--jm-sage-hover);
    color: #f4efe6;
  }
  &&:disabled {
    background-color: var(--jm-sage);
    border-color: var(--jm-sage);
  }

  @media (min-width: 768px) {
    && {
      width: auto;
      min-width: 200px;
    }
  }
`;

const ButtonWrapper = styled.div`
  margin-top: 20px;

  @media (min-width: 768px) {
    display: flex;
    justify-content: flex-end;
  }
`;

// Inline "thank you" state shown in place of the form after a successful send.
const ThankYou = styled.div`
  text-align: center;
  padding: 32px 12px;
`;

const Floret = styled.div`
  font-family: var(--jm-display);
  font-size: 44px;
  line-height: 1;
  color: var(--jm-sage-deep);
`;

const ThankYouHeading = styled.h3`
  font-family: var(--jm-display);
  font-weight: 500;
  font-size: 30px;
  color: var(--jm-ink);
  margin: 14px 0 8px;
`;

const ThankYouCopy = styled.p`
  font-family: var(--jm-sans);
  font-size: 15px;
  line-height: 1.6;
  color: var(--jm-ink-soft);
  margin: 0;
`;

// Visually hidden honeypot — the primary bot gate. Humans never see or tab to
// it; a bot that fills it gets silently dropped, server-side and client-side.
const HoneypotField = styled.div`
  position: absolute;
  left: -5000px;
  height: 0;
  overflow: hidden;
`;

const Questionaire = ({
  questions: { questionsMain, questionsSecondary, secondaryPrompt },
  toasterFuctions,
}: QuestionaireProps) => {
  const [mainQuestions, setQuestionsMain] =
    useState<QuestionType[]>(questionsMain);
  const { failed, serverFailed } = toasterFuctions;

  // Inline success — replaces the form with a thank-you card on send.
  const [sent, setSent] = useState<boolean>(false);

  const [secondaryQuestions, setQuestionsSecondary] =
    useState<QuestionType[]>(questionsSecondary);

  const [showSecondaryQuestions, setShowSecondaryQuestions] =
    useState<boolean>(false);

  const [sending, setSending] = useState<boolean>(false);

  // Honeypot value — stays empty for real users.
  const [botField, setBotField] = useState<string>("");

  // When the form first mounted. The server requires a minimum elapsed time
  // before a submit is accepted — bots fire near-instantly, humans don't.
  const mountedAtRef = useRef<number>(Date.now());

  const handleSubmit = (event: Event) => {
    event.preventDefault();

    // Guard against double-clicks while a send is in flight.
    if (sending) {
      return;
    }

    // Honeypot tripped — pretend success, send nothing.
    if (botField !== "") {
      setSent(true);
      return;
    }

    let validEmail = true;
    let name = "";
    let email = "";

    const mQ = mainQuestions.map((question) => {
      const { value, required, type, id } = question;
      const invalidResponse = required && value === "";
      question.invalid = invalidResponse;

      if (type === "email") {
        email = value;
        question.invalid = validateEmail(value) === false;
        validEmail = !question.invalid;
      }

      if (type === "name") {
        name = value;
      }

      if (type === "date") {
        question.invalid = validateDate(value) === false;
        validEmail = !question.invalid;
      }

      if (id === "number") {
        question.invalid = validatePhoneNumber(value) === false;
        validEmail = !question.invalid;
      }

      return question;
    });
    const sQ = secondaryQuestions.map((question) => {
      const { value, required } = question;
      question.invalid = required && value === "";
      if (question.invalid) {
        validEmail = false;
      }
      return question;
    });
    setQuestionsMain(mQ);
    setQuestionsSecondary(sQ);

    if (!validEmail) {
      failed(true);
      return;
    }

    const payload = {
      from_name: name,
      reply_to: email,
      to_name: "Jeannie",
      message: generateEmailMessage(mainQuestions, secondaryQuestions),
      // Bot gates verified server-side: honeypot must be empty, and at least
      // a few seconds must have elapsed since the form mounted.
      company: botField,
      elapsed_ms: Date.now() - mountedAtRef.current,
    };

    setSending(true);

    fetch(CONTACT_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then((response) => {
        if (response.ok) {
          setSent(true);
        } else {
          serverFailed(true);
        }
        setSending(false);
      })
      .catch(() => {
        serverFailed(true);
        setSending(false);
      });
  };

  return (
    <QuestionaireWrapper id="contact-section">
      <Layout>
        <Intro>
          <Eyebrow>Let&rsquo;s begin</Eyebrow>
          <IntroHeading>Start the conversation</IntroHeading>
          <IntroCopy>
            Tell me a little about your day and the feeling you&rsquo;re after.
            The more you share, the better I can bring your floral vision to
            life &mdash; I&rsquo;ll be in touch soon.
          </IntroCopy>
        </Intro>
        <Card>
          {sent ? (
            <ThankYou>
              <Floret>&#10047;</Floret>
              <ThankYouHeading>Thank you</ThankYouHeading>
              <ThankYouCopy>
                Your inquiry is in the studio &mdash; I&rsquo;ll be in touch
                soon.
              </ThankYouCopy>
            </ThankYou>
          ) : (
            <Form>
            <HoneypotField aria-hidden="true">
              <label>
                Company
                <input
                  type="text"
                  name="company"
                  tabIndex={-1}
                  autoComplete="off"
                  value={botField}
                  onChange={(event) => setBotField(event.target.value)}
                />
              </label>
            </HoneypotField>
            <QuestionsMain
              questionsMain={mainQuestions}
              setQuestionsMain={setQuestionsMain}
            />
            <AdditionalQuestionsPrompt
              secondaryPrompt={secondaryPrompt}
              showSecondaryQuestions={showSecondaryQuestions}
              setShowSecondaryQuestions={setShowSecondaryQuestions}
            />
            {showSecondaryQuestions ? (
              <QuestionsSecondary
                questionsSecondary={secondaryQuestions}
                setQuestionsSecondary={setQuestionsSecondary}
              />
            ) : (
              ""
            )}
            <ButtonWrapper>
              <ButtonStyled
                variant="primary"
                type="submit"
                disabled={sending}
                onClick={handleSubmit}
              >
                {sending ? "Sending…" : "Send inquiry"}
              </ButtonStyled>
            </ButtonWrapper>
            </Form>
          )}
        </Card>
      </Layout>
    </QuestionaireWrapper>
  );
};

export default Questionaire;
